import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ActivityService, Activity } from '../activities/activity.service';
import { DeliveryService } from '../deliveries/delivery.service';
import { MaintenanceService, MaintenanceStatusDto } from '../maintenance/maintenance.service';
import { RiderService } from '../riders/rider.service';
import { VehicleService } from '../vehicles/vehicle.service';

@Component({
  selector: 'app-logistics-overview',
  templateUrl: './logistics-overview.component.html',
  styleUrls: ['./logistics-overview.component.scss']
})
export class LogisticsOverviewComponent implements OnInit {
  loading = false;
  deliveries: any[] = [];
  riders: any[] = [];
  riderAnalytics: any = null;
  vehicles: any[] = [];
  vehicleAnalytics: any = null;
  maintenance: MaintenanceStatusDto[] = [];
  activities: Activity[] = [];

  constructor(
    private deliveryService: DeliveryService,
    private riderService: RiderService,
    private vehicleService: VehicleService,
    private maintenanceService: MaintenanceService,
    private activityService: ActivityService
  ) {}

  ngOnInit(): void {
    this.loadOverview();
  }

  loadOverview(): void {
    this.loading = true;
    forkJoin({
      deliveries: this.deliveryService.getToday(),
      riders: this.riderService.getRiders(),
      riderAnalytics: this.riderService.getPhoneAnalytics(),
      vehicles: this.vehicleService.getVehicles(),
      vehicleAnalytics: this.vehicleService.getMeterAnalytics(),
      maintenance: this.maintenanceService.check(),
      activities: this.activityService.getAll()
    }).subscribe({
      next: ({ deliveries, riders, riderAnalytics, vehicles, vehicleAnalytics, maintenance, activities }) => {
        this.deliveries = deliveries || [];
        this.riders = riders || [];
        this.riderAnalytics = riderAnalytics || null;
        this.vehicles = vehicles || [];
        this.vehicleAnalytics = vehicleAnalytics || null;
        this.maintenance = maintenance || [];
        this.activities = activities || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load logistics overview.', 'error');
      }
    });
  }

  get activeRiders(): number {
    return this.riders.filter((row) => row?.isActive).length;
  }

  get activeVehicles(): number {
    return this.vehicles.filter((row) => row?.isActive).length;
  }

  get unpaidDeliveries(): number {
    return this.deliveries.filter((row) => !row?.isPaid).length;
  }

  get cancelledDeliveries(): number {
    return this.deliveries.filter((row) => row?.isCancelled || String(row?.status || '').toLowerCase() === 'cancelled').length;
  }

  get maintenanceRiskCount(): number {
    return this.maintenance.filter((row) => row?.needsMaintenance).length;
  }

  get inflightActivities(): number {
    return this.activities.filter((row) => {
      const status = String(row?.status || '').toLowerCase();
      return status === 'assigned' || status === 'in transit' || status === 'pending';
    }).length;
  }

  get revenueToday(): number {
    return this.deliveries.reduce((sum, row) => sum + Number(row?.amount || 0), 0);
  }

  get recentDeliveries(): any[] {
    return [...this.deliveries]
      .sort((a, b) => new Date(b.orderDate || b.createdAt || 0).getTime() - new Date(a.orderDate || a.createdAt || 0).getTime())
      .slice(0, 6);
  }

  get routePressure(): Array<{ label: string; value: number }> {
    const map = new Map<string, number>();
    this.deliveries.forEach((row) => {
      const key = `${row?.pickupLocation || 'Unknown'} → ${row?.deliveryLocation || 'Unknown'}`;
      map.set(key, (map.get(key) || 0) + 1);
    });

    return Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }

  get riderLoad(): Array<{ label: string; value: number; sublabel: string }> {
    const map = new Map<string, { count: number; creditUsed: number }>();

    this.activities.forEach((row) => {
      const key = String(row?.assignedRider || 'Unassigned').trim() || 'Unassigned';
      const current = map.get(key) || { count: 0, creditUsed: 0 };
      current.count += 1;
      map.set(key, current);
    });

    this.riders.forEach((row) => {
      const key = String(row?.fullName || 'Unknown Rider').trim() || 'Unknown Rider';
      const current = map.get(key) || { count: 0, creditUsed: 0 };
      current.creditUsed = Number(row?.creditUsed || current.creditUsed || 0);
      map.set(key, current);
    });

    return Array.from(map.entries())
      .map(([label, value]) => ({
        label,
        value: value.count,
        sublabel: `Credit used: ${value.creditUsed.toFixed(0)}`
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 6);
  }

  get watchlist(): MaintenanceStatusDto[] {
    return [...this.maintenance]
      .sort((a, b) => Number(b.daysSinceLastMaintenance || 0) - Number(a.daysSinceLastMaintenance || 0))
      .slice(0, 6);
  }
}
